import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Minus, Plus, Pencil, X, LogOut, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, friendlyError, clearGuestToken } from "@/lib/api";
import { Brand } from "@/components/Nav";
import { DISH_CATEGORIES, DIETARY_OPTIONS, STATUS_LABELS } from "@/lib/constants";
import { useEvent } from "@/hooks/useEvent";

function StatusBadge({ status }) {
  const map = {
    attending: "bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20",
    declined: "bg-inkMuted/10 text-inkSoft border-hair",
    cancelled: "bg-[#D97706]/10 text-[#D97706] border-[#D97706]/20",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-sm font-semibold ${map[status]}`} data-testid="guest-status-badge">
      {status === "attending" && <Check size={15} />} {STATUS_LABELS[status]}
    </span>
  );
}

export function GuestDashboard({ onSignOut }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { data: event } = useEvent();
  const [editing, setEditing] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const { data: rsvp, isLoading, error } = useQuery({
    queryKey: ["guest-rsvp"],
    queryFn: async () => (await api.get("/guest/rsvp")).data,
    retry: false,
  });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["guest-rsvp"] }); qc.invalidateQueries({ queryKey: ["event"] }); qc.invalidateQueries({ queryKey: ["table"] }); qc.invalidateQueries({ queryKey: ["guests"] }); };

  const signOut = () => { clearGuestToken(); onSignOut?.(); };

  const doCancel = async () => {
    try { await api.post("/guest/rsvp/cancel"); toast.success("Your RSVP has been updated ✓"); setConfirmCancel(false); refresh(); }
    catch (e) { toast.error(friendlyError(e)); }
  };
  const doReactivate = async () => {
    try { await api.post("/guest/rsvp/reactivate"); toast.success("Yay! You're back on the list 🎉"); refresh(); }
    catch (e) { toast.error(friendlyError(e)); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-cream grid place-items-center px-5 text-center">
        <div>
          <p className="text-inkSoft">Your session expired. Please verify your number again.</p>
          <button onClick={signOut} className="mt-4 gold-gradient text-espressoDeep font-bold px-6 py-3 rounded-full" data-testid="session-expired-signin">Sign in again</button>
        </div>
      </div>
    );
  }
  if (isLoading || !rsvp) return <div className="min-h-screen bg-cream grid place-items-center"><div className="w-10 h-10 rounded-full border-2 border-gold border-t-transparent animate-spin" /></div>;

  const guests = [rsvp.primary_name, ...(rsvp.guests || [])];

  return (
    <div className="min-h-screen bg-cream">
      <div className="border-b border-hair bg-cream/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center justify-between">
          <Brand />
          <button onClick={signOut} className="flex items-center gap-2 text-sm text-inkSoft hover:text-espresso" data-testid="guest-signout"><LogOut size={16} /> Sign out</button>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-5 py-8">
        <p className="text-eyebrow text-gold">Welcome back</p>
        <h1 className="font-serif text-4xl text-espresso mt-1" data-testid="guest-welcome">{rsvp.primary_name.split(" ")[0]} 👋</h1>
        <p className="text-inkSoft mt-2">Here's your Friendsgiving RSVP.</p>

        <div className="mt-6 rounded-3xl bg-white border border-hair shadow-soft overflow-hidden" data-testid="guest-rsvp-card">
          <div className="p-6 flex items-center justify-between">
            <div>
              <p className="text-eyebrow text-inkMuted mb-2">Status</p>
              <StatusBadge status={rsvp.status} />
            </div>
            {rsvp.status !== "cancelled" && rsvp.status !== "declined" && (
              <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 text-sm font-semibold text-gold hover:underline" data-testid="guest-edit"><Pencil size={15} /> Edit</button>
            )}
          </div>

          {rsvp.status === "attending" && (
            <>
              <Cell label="Your Party" value={`${rsvp.party_size} ${rsvp.party_size === 1 ? "Person" : "People"}`} testid="guest-party" />
              <Cell label="Your Guests" testid="guest-guests">
                <div className="space-y-1">
                  {guests.map((g, i) => <p key={i} className="text-espresso font-medium">{g}{i === 0 && <span className="text-inkMuted text-sm"> · you</span>}</p>)}
                </div>
              </Cell>
              <Cell label="You're Bringing" testid="guest-dish">
                {rsvp.dish_active && rsvp.dish ? (
                  <p className="text-espresso font-medium">{DISH_CATEGORIES.find((c) => c.id === rsvp.dish.category)?.icon} {rsvp.dish.name}</p>
                ) : <p className="text-inkSoft">Nothing this time</p>}
              </Cell>
              <Cell label="Dietary Notes" value={rsvp.dietary?.length ? [...rsvp.dietary, rsvp.dietary_notes].filter(Boolean).join(", ") : "None"} testid="guest-dietary" last />
            </>
          )}
        </div>

        {/* address unlocked after RSVP */}
        {rsvp.event_address && rsvp.status === "attending" && (
          <div className="mt-4 rounded-2xl bg-espressoDeep text-cream p-5" data-testid="guest-address">
            <p className="text-eyebrow text-goldLight/70 mb-1">📍 The Location</p>
            <p className="font-medium">{rsvp.event_address}</p>
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(rsvp.event_address)}`} target="_blank" rel="noreferrer" className="inline-block mt-3 text-sm font-semibold text-goldLight hover:underline" data-testid="guest-directions">Get Directions →</a>
          </div>
        )}

        <div className="mt-6 space-y-3">
          {rsvp.status === "attending" && (
            <button onClick={() => setEditing(true)} className="w-full gold-gradient text-espressoDeep font-bold py-4 rounded-full shadow-gold active:scale-95 transition" data-testid="guest-edit-btn">Edit RSVP</button>
          )}
          {(rsvp.status === "cancelled" || rsvp.status === "declined") && (
            <button onClick={doReactivate} className="w-full gold-gradient text-espressoDeep font-bold py-4 rounded-full shadow-gold active:scale-95 transition" data-testid="guest-reactivate">I can make it after all 🎉</button>
          )}
          {rsvp.status === "attending" && (
            <button onClick={() => setConfirmCancel(true)} className="w-full border border-hair bg-white text-[#D97706] font-semibold py-4 rounded-full hover:border-[#D97706]/40 active:scale-95 transition" data-testid="guest-cancel">I can't make it anymore</button>
          )}
          <button onClick={() => nav("/")} className="w-full py-3 text-inkSoft font-medium hover:text-espresso" data-testid="guest-back-home">Back to Invitation</button>
        </div>

        <p className="text-center text-xs text-inkMuted mt-6 flex items-center justify-center gap-1.5"><CalendarClock size={13} /> Last updated {new Date(rsvp.updated_at).toLocaleDateString()}</p>
      </div>

      <AnimatePresence>
        {editing && <EditModal rsvp={rsvp} event={event} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); refresh(); }} />}
      </AnimatePresence>

      <AnimatePresence>
        {confirmCancel && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 grid place-items-center bg-espressoDeep/60 backdrop-blur-sm p-6">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="bg-cream rounded-3xl p-8 max-w-sm w-full text-center" data-testid="cancel-modal">
              <h3 className="font-serif text-2xl text-espresso">Are you sure you want to change your RSVP?</h3>
              <p className="text-inkSoft mt-2">We'll mark you as no longer attending. You can always come back later.</p>
              <div className="mt-6 space-y-3">
                <button onClick={doCancel} className="w-full bg-[#D97706] text-white font-bold py-3.5 rounded-full active:scale-95 transition" data-testid="cancel-confirm">Yes, update my RSVP</button>
                <button onClick={() => setConfirmCancel(false)} className="w-full py-3 text-inkSoft font-medium hover:text-espresso" data-testid="cancel-nevermind">Never mind</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Cell({ label, value, children, testid, last }) {
  return (
    <div className={`px-6 py-4 border-t border-hair`} data-testid={testid}>
      <p className="text-eyebrow text-inkMuted mb-1.5">{label}</p>
      {value ? <p className="text-espresso font-medium">{value}</p> : children}
    </div>
  );
}

function EditModal({ rsvp, event, onClose, onSaved }) {
  const [party, setParty] = useState(rsvp.party_size);
  const [guests, setGuests] = useState(rsvp.guests || []);
  const [bringing, setBringing] = useState(rsvp.bringing_dish);
  const [dish, setDish] = useState(rsvp.dish || { category: "Main Dish", name: "" });
  const [dietary, setDietary] = useState(rsvp.dietary || []);
  const [notes, setNotes] = useState(rsvp.dietary_notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/guest/rsvp", {
        party_size: party, guests: guests.slice(0, party - 1).filter((g) => g?.trim() !== undefined),
        bringing_dish: bringing, dish: bringing && dish.name ? dish : null,
        dietary, dietary_notes: notes,
      });
      toast.success("Your RSVP has been updated ✓");
      onSaved();
    } catch (e) { toast.error(friendlyError(e)); } finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-espressoDeep/60 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose}>
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }} onClick={(e) => e.stopPropagation()}
        className="bg-cream w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto" data-testid="edit-modal">
        <div className="sticky top-0 bg-cream/95 backdrop-blur px-6 py-4 border-b border-hair flex items-center justify-between">
          <h3 className="font-serif text-2xl text-espresso">Edit RSVP</h3>
          <button onClick={onClose} className="text-inkMuted hover:text-espresso" data-testid="edit-close"><X /></button>
        </div>
        <div className="px-6 py-6 space-y-8">
          <div>
            <p className="text-eyebrow text-inkMuted mb-3">Party Size</p>
            <div className="flex items-center gap-6">
              <button onClick={() => { setParty(Math.max(1, party - 1)); }} disabled={party <= 1} className="w-12 h-12 rounded-full bg-white border border-hair grid place-items-center disabled:opacity-30 active:scale-90 transition" data-testid="edit-party-minus"><Minus size={20} /></button>
              <span className="font-serif text-4xl text-espresso w-10 text-center" data-testid="edit-party-count">{party}</span>
              <button onClick={() => setParty(Math.min(event?.max_party_size || 8, party + 1))} disabled={party >= (event?.max_party_size || 8)} className="w-12 h-12 rounded-full gold-gradient grid place-items-center disabled:opacity-30 active:scale-90 transition" data-testid="edit-party-plus"><Plus size={20} /></button>
            </div>
          </div>

          {party > 1 && event?.allow_additional_guests && (
            <div>
              <p className="text-eyebrow text-inkMuted mb-3">Guest Names</p>
              <div className="space-y-2">
                {Array.from({ length: party - 1 }).map((_, i) => (
                  <input key={i} value={guests[i] || ""} placeholder={`Guest ${i + 2}`} data-testid={`edit-guest-${i}`}
                    onChange={(e) => { const g = [...guests]; g[i] = e.target.value; setGuests(g); }}
                    className="w-full bg-white border border-hair rounded-xl px-4 py-3 text-espresso focus:border-gold outline-none" />
                ))}
              </div>
            </div>
          )}

          {event?.dishes_enabled && (
            <div>
              <p className="text-eyebrow text-inkMuted mb-3">Dish</p>
              <div className="flex gap-2 mb-3">
                <button onClick={() => setBringing(true)} className={`flex-1 py-3 rounded-xl font-semibold border transition ${bringing ? "gold-gradient text-espressoDeep border-transparent" : "bg-white border-hair text-espresso"}`} data-testid="edit-bringing-yes">Bringing a dish</button>
                <button onClick={() => setBringing(false)} className={`flex-1 py-3 rounded-xl font-semibold border transition ${!bringing ? "bg-espresso text-cream border-transparent" : "bg-white border-hair text-espresso"}`} data-testid="edit-bringing-no">Not this time</button>
              </div>
              {bringing && (
                <div className="space-y-3">
                  <select value={dish.category} onChange={(e) => setDish({ ...dish, category: e.target.value })} className="w-full bg-white border border-hair rounded-xl px-4 py-3 text-espresso focus:border-gold outline-none" data-testid="edit-dish-category">
                    {DISH_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                  </select>
                  <input value={dish.name} onChange={(e) => setDish({ ...dish, name: e.target.value })} placeholder="Baked Mac & Cheese" className="w-full bg-white border border-hair rounded-xl px-4 py-3 text-espresso focus:border-gold outline-none" data-testid="edit-dish-name" />
                </div>
              )}
            </div>
          )}

          {event?.dietary_enabled && (
            <div>
              <p className="text-eyebrow text-inkMuted mb-3">Dietary Info</p>
              <div className="flex flex-wrap gap-2">
                {DIETARY_OPTIONS.map((d) => {
                  const active = dietary.includes(d);
                  return <button key={d} data-testid={`edit-dietary-${d.replace(/[^a-zA-Z]/g, "")}`}
                    onClick={() => setDietary(d === "None" ? (active ? [] : ["None"]) : active ? dietary.filter((x) => x !== d) : [...dietary.filter((x) => x !== "None"), d])}
                    className={`px-4 py-2 rounded-full border text-sm font-medium transition ${active ? "gold-gradient text-espressoDeep border-transparent" : "bg-white border-hair text-espresso"}`}>{d}</button>;
                })}
              </div>
              {(dietary.includes("Food Allergy") || dietary.includes("Other")) && (
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Tell us more…" rows={2} className="w-full mt-3 bg-white border border-hair rounded-xl px-4 py-3 text-espresso focus:border-gold outline-none" data-testid="edit-dietary-notes" />
              )}
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-cream/95 backdrop-blur px-6 py-4 border-t border-hair">
          <button onClick={save} disabled={saving} className="w-full gold-gradient text-espressoDeep font-bold py-4 rounded-full shadow-gold active:scale-95 transition disabled:opacity-40" data-testid="edit-save">{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      </motion.div>
    </motion.div>
  );
}
